import React, {SyntheticEvent} from 'react';
import {useTranslation} from 'react-i18next';
import {useHistory, useLocation} from 'react-router-dom';
import {useForm, FormProvider} from 'react-hook-form';
import {TFunction} from 'i18next';
import _ from 'lodash';
import {useReservation} from 'context/reservation';
import {getNumberOfGuests} from 'utils/reservation';
import {PATHS} from '../../../Routes';
import {TabTypes} from 'components/acommon/Tabs';
import {TestIds} from '../../PaymentsDetailsForm/utils';
import {NONE_EXEMPTION_OPTION} from '../../../utils/constants';
import {useErrorModal, useIsMounted, useModalControls, useStatus} from 'utils/hooks';
import {usePaymentSettings} from '../../../hooks';
import {useTaxes} from '../useTaxes';
import {useComputedReservationDetails} from '../../../context/computedReservationDetails';
import Button from 'components/acommon/Button';
import Modal from 'components/acommon/Modal';
import TaxesWarningModal from '../TaxesWarningModal';
import TouristTaxesCard from '../TouristTaxesCard';
import {Total} from '../../PaymentsDetailsForm/components/Summary';
import {Content, StyledModalForm, TabsTaxes, TaxesModalTitle} from './styled';
import {
  FormTypes,
  FORM_NAMES,
  LocationStateTaxes,
  FORM_NAMES_GUEST_TAX,
} from '../TouristTaxesSetup/TouristTaxesSetup';

type ClickEvent = SyntheticEvent<HTMLButtonElement, Event>;

const defaultFormValues = {
  [FORM_NAMES.guestsTaxes]: [
    {
      [FORM_NAMES_GUEST_TAX.age]: '',
      [FORM_NAMES_GUEST_TAX.exemption]: undefined,
      [FORM_NAMES_GUEST_TAX.total]: 0,
    },
  ],
};

const getTabs = (amountOfGuests = 0, t: TFunction) => {
  return _.range(amountOfGuests).map((n, idx) => ({
    label: `${t('guest')} ${idx + 1 < 10 ? `0${idx + 1}` : `${idx + 1}`}`,
    type: TabTypes.button,
    idx,
  }));
};

const useTabInfo = (t: TFunction) => {
  const {data: reservation} = useReservation();
  const [currentTab, setCurrentTab] = React.useState(0);

  const TABS = getTabs(getNumberOfGuests(reservation, 1), t);
  const isLastTab = currentTab + 1 >= TABS.length;

  return {currentTab, setCurrentTab, TABS, isLastTab};
};

function TouristTaxesModal() {
  const {t} = useTranslation();
  const history = useHistory();
  const isMounted = useIsMounted();
  const {isLoading, setStatus} = useStatus();
  const location = useLocation<LocationStateTaxes>();
  const {ErrorModal, displayError} = useErrorModal();
  const {hasTaxes, reservation} = useComputedReservationDetails();
  const isCalculateTaxesCompleted = parseInt(reservation.dynamic_tax, 10) > 0;
  const isVisible = location.pathname.includes(PATHS.payTouristTaxes) && hasTaxes;

  const {
    isOpen: isWarningModalOpen,
    openModal: openWarningModal,
    closeModal: closeWarningModal,
  } = useModalControls();
  const {paymentSettingsCurrencySign: currency, paymentSettingsCurrency: currencyLabel} =
    usePaymentSettings();
  const methods = useForm<FormTypes>({
    defaultValues: defaultFormValues,
    mode: 'onChange',
  });
  const {
    getValues,
    setValue,
    reset,
    handleSubmit,
    watch,
    formState: {isDirty},
  } = methods;
  const totalPriceValue = watch(FORM_NAMES.guestsTaxes);

  const {
    guestsTaxes,
    calculateAndSetGuestTax,
    getUpdatedLocalLocationState,
    isLoading: isSubmitting,
    availableTaxExemptionsOptions,
    calculateAllGuestTax,
  } = useTaxes({
    disabled: !isVisible,
    onError: displayError,
  });

  const {currentTab, setCurrentTab, TABS, isLastTab} = useTabInfo(t);

  React.useEffect(
    function redirectFromTaxes() {
      if (!hasTaxes && isVisible) {
        history.push(PATHS.pay, location.state);
      }
    },
    [hasTaxes, history, isVisible, location.state],
  );

  React.useEffect(
    function preloadTaxes() {
      if (!guestsTaxes) return;

      const guestsTaxesList = guestsTaxes.guest_list.map(tax => {
        const exemptionOption = availableTaxExemptionsOptions.find(
          option => option.value === tax.exemption,
        );

        return {
          [FORM_NAMES_GUEST_TAX.age]: tax.age,
          [FORM_NAMES_GUEST_TAX.exemption]: exemptionOption,
          [FORM_NAMES_GUEST_TAX.total]: 0,
        };
      });

      reset({[FORM_NAMES.guestsTaxes]: guestsTaxesList});
    },
    [availableTaxExemptionsOptions, guestsTaxes, reset],
  );

  React.useEffect(
    function preloadTaxes() {
      if (guestsTaxes) {
        const guestsTaxesList = guestsTaxes.guest_list?.map(tax => {
          const exemptionOption = availableTaxExemptionsOptions.find(
            option => option.value === tax.exemption,
          );

          return {
            [FORM_NAMES_GUEST_TAX.age]: tax.age,
            [FORM_NAMES_GUEST_TAX.exemption]: exemptionOption,
            [FORM_NAMES_GUEST_TAX.total]: tax.total,
          };
        });
        reset({[FORM_NAMES.guestsTaxes]: guestsTaxesList});
      }
    },
    [availableTaxExemptionsOptions, guestsTaxes, reset],
  );

  const calculateGuestsTax = React.useCallback(async () => {
    const data = await calculateAllGuestTax(getValues() as FormTypes);

    if (!isMounted.current) return;

    const guestListWithoutExemptions = data?.guest_list.filter(
      guest => guest.exemption === NONE_EXEMPTION_OPTION.value,
    );
    const total = Number(data?.amount) / Number(guestListWithoutExemptions?.length);

    data?.guest_list?.forEach((guest, index) => {
      const itemTotalName =
        `${FORM_NAMES.guestsTaxes}.${index}.${FORM_NAMES_GUEST_TAX.total}` as const;
      const value = guest.exemption !== NONE_EXEMPTION_OPTION.value ? 0 : total || 0;
      // @ts-ignore
      setValue(itemTotalName, value);
    });
  }, [calculateAllGuestTax, getValues, isMounted, setValue]);

  const totalPrice = Object.values(totalPriceValue || []).reduce(
    (sum, tax) => sum + (tax.total || 0),
    0,
  );

  const tabsMemo = React.useMemo(() => {
    return TABS.map((a, index) => {
      const guestTaxItemName = `${FORM_NAMES.guestsTaxes}.${index}` as const;

      return (
        <div key={guestTaxItemName}>
          <TouristTaxesCard
            setTotalStatus={setStatus}
            guestItemName={guestTaxItemName}
            taxExemptionsOptions={availableTaxExemptionsOptions}
            calculateGuestsTax={calculateGuestsTax}
            index={index}
          />
          <Total
            currency={currency}
            currencyLabel={currencyLabel}
            title={t('total_taxes')}
            price={totalPrice}
          />
        </div>
      );
    });
  }, [
    TABS,
    calculateGuestsTax,
    availableTaxExemptionsOptions,
    currency,
    currencyLabel,
    setStatus,
    t,
    totalPrice,
  ]);

  const goNextLocation = (data: FormTypes) => {
    if (isLastTab) history.push(PATHS.pay, getUpdatedLocalLocationState(data));
    else setCurrentTab(currentTab + 1);
  };

  const withPreventDefault =
    (callback: any) =>
    (e: ClickEvent, ...otherArgs: any[]) => {
      e.preventDefault();
      callback(e, ...otherArgs);
    };

  const handleTabClick = withPreventDefault((e: any, path: string, idx: number) => {
    setCurrentTab(idx);
  });

  const handleNextClick = withPreventDefault(() => {
    handleSubmit(goNextLocation)();
  });

  const handleDiscardTaxes = () => {
    const newData = getValues();
    closeWarningModal();
    reset();
    if (isCalculateTaxesCompleted) {
      return history.push(PATHS.pay, location.state);
    }
    history.push(PATHS.pay, getUpdatedLocalLocationState(newData));
  };

  const onSubmit = (data: FormTypes) => {
    calculateAndSetGuestTax(data, totalPrice);
  };

  const handleSaveWarningModal = () => {
    handleSubmit(onSubmit)();
    closeWarningModal();
  };

  const handleCloseModal = () => {
    if (isDirty) return openWarningModal();
    setCurrentTab(0);
    reset();
    history.push(PATHS.pay, location.state);
  };

  if (!isMounted || !isVisible) return null;
  return (
    <Modal
      data-testid={TestIds.taxesModal}
      visible={isVisible}
      onClose={handleCloseModal}
    >
      <TaxesModalTitle>{t('tourist_taxes')}</TaxesModalTitle>

      <FormProvider {...methods}>
        <StyledModalForm onSubmit={handleSubmit(goNextLocation)}>
          <TabsTaxes items={TABS} currentItemActive={currentTab} onClick={handleTabClick}>
            <Content children={tabsMemo[currentTab]} />
          </TabsTaxes>
          {isLastTab ? (
            <Button
              disabled={isLoading || isSubmitting}
              onClick={handleSubmit(onSubmit)}
              children={t('update_all')}
            />
          ) : (
            <Button
              disabled={isLoading || isSubmitting}
              onClick={handleNextClick}
              children={t('next_guest')}
            />
          )}
        </StyledModalForm>
      </FormProvider>
      <ErrorModal />
      <TaxesWarningModal
        isOpen={isWarningModalOpen}
        onClose={closeWarningModal}
        onSave={handleSaveWarningModal}
        onDiscard={handleDiscardTaxes}
      />
    </Modal>
  );
}

export {TouristTaxesModal};
